import React, { useState } from "react";

import { Typography, styled } from "@mui/material";

import * as Events from "@app/analytics/tracking/events";

import Button from "@rui/Button";
import Stack from "@rui/Stack";
import Switch from "@rui/Switch";

import type {
  CreateWorkspaceSetting,
  CreateWorkspaceSettings,
  UpdateCreateWorkspaceSetting,
  UpdateCreateWorkspaceSettings,
  WizardFormProps,
} from "./types";
import WizardTitle from "../../components/WizardTitle";
import WorkspaceSetting from "../components/WorkspaceSetting";
import { WorkspaceSettingCategory, WORKSPACE_SETTINGS } from "../constants";

const EditButton = styled(Button)(({ theme }) => ({
  height: "36px",
  borderRadius: "4px",
  transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  "&:hover": {
    backgroundColor: "transparent",
    border: `solid 1px ${theme.palette.shades.primary[500]}`,
  },
}));

const SECTIONS: { id: WorkspaceSettingCategory; label: string }[] = [
  {
    id: "report",
    label: "Report settings",
  },
  {
    id: "sharing",
    label: "Report sharing",
  },
  {
    id: "access",
    label: "Access",
  },
];

const ReviewSettingsStep = ({
  onNextStep,
  loading,
  workspaceSettings,
  updateWorkspaceSettings,
}: WizardFormProps & {
  workspaceSettings: CreateWorkspaceSettings;
  updateWorkspaceSettings: <T extends UpdateCreateWorkspaceSetting>(
    setting: T,
    value: UpdateCreateWorkspaceSettings[T]
  ) => void;
}) => {
  const [openModalId, setOpenModalId] = useState<string | null>(null);
  const handleSubmit = () => onNextStep();

  return (
    <Stack spacing={3} variant="left">
      <WizardTitle
        title="Review Workspace default settings"
        subtitle="We copied some settings from your account to use in your Workspace. Please confirm that everyone in your Workspace should use these default settings:"
      />
      {SECTIONS.map((section) => (
        <Stack spacing={1.5} key={section.id} width="100%">
          <Typography variant="bodySecondaryMed">{section.label}</Typography>
          {WORKSPACE_SETTINGS[section.id].map((setting) => {
            const currentSettingLabel = setting.getLabel(workspaceSettings);

            return (
              <React.Fragment key={setting.id}>
                <WorkspaceSetting
                  {...setting}
                  currentSettingLabel={currentSettingLabel}
                  control={
                    setting.Modal ? (
                      <EditButton
                        variant="text"
                        onClick={() => setOpenModalId(setting.id)}
                      >
                        Edit
                      </EditButton>
                    ) : (
                      <Switch
                        checked={
                          workspaceSettings[
                            setting.id as CreateWorkspaceSetting
                          ] as boolean
                        }
                        onChange={(_e, checked) => {
                          Events.workspaceWizardSettings({
                            setting: setting.id,
                            oldValue:
                              workspaceSettings[
                                setting.id as CreateWorkspaceSetting
                              ],
                            newValue: checked,
                          });
                          updateWorkspaceSettings(setting.id, checked);
                        }}
                      />
                    )
                  }
                />
                {setting.Modal && (
                  <setting.Modal
                    open={openModalId === setting.id}
                    onClose={() => setOpenModalId(null)}
                    onConfirm={(newValue) => {
                      Events.workspaceWizardSettings({
                        setting: setting.id,
                        oldValue:
                          workspaceSettings[
                            setting.id as CreateWorkspaceSetting
                          ],
                        newValue,
                      });
                      setOpenModalId(null);
                      updateWorkspaceSettings(setting.id, newValue);
                    }}
                    workspaceSettings={workspaceSettings}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Stack>
      ))}
      <Button
        sx={{ minWidth: "200px" }}
        onClick={handleSubmit}
        loading={loading}
      >
        Finish
      </Button>
    </Stack>
  );
};

export default ReviewSettingsStep;
